import { DocLink } from "../components/Legal";
import type { ReactNode } from "react";
import { ApiError, get } from "../api/client";
import { useLoad, usePoll } from "../api/hooks";
import type { BetaStatus } from "../api/types";
import { BetaFull } from "../components/BetaFull";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "../auth/AuthContext";
import { googleConfigured, signInDev, signInWithGoogle } from "../auth/google";
import { useTurnstile } from "../auth/Turnstile";
import { dev } from "../api/client";

/** An address not on the list (403): name the account Google chose and say what to do. */
function refusal(e: unknown): ReactNode {
  if (e instanceof ApiError && e.code === "FORBIDDEN") {
    const email = typeof e.details?.["email"] === "string" ? e.details["email"] : "This Google account";
    return <>{email} is not on the beta list yet. Sign in with the address you gave us, or write to <a className="doc" href="mailto:admin@deepthird.com">admin@deepthird.com</a>.</>;
  }
  return (e as Error).message;
}

/** 01 · Sign in (chosen A): product name, one sentence, one button; the beta terms and the privacy policy linked under it. */
export default function SignIn() {
  const nav = useNavigate();
  const loc = useLocation();
  const { refresh } = useAuth();
  const status = useLoad(() => get<BetaStatus>("/v1/beta/status"), []);
  usePoll(status.reload, 30_000, true);
  const full = status.data?.enabled && status.data.full;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ReactNode>(null);
  const [devOpen, setDevOpen] = useState(false);
  const [email, setEmail] = useState("demo@deepthird.local");
  const [plan, setPlan] = useState<"free" | "basic" | "pro">("free");
  const verification = useTurnstile(googleConfigured);
  const from = (loc.state as { from?: string } | null)?.from || "/";

  const go = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refresh();
      nav(from, { replace: true });
    } catch (e) {
      setError(refusal(e));
    } finally {
      verification.reset();
      setBusy(false);
    }
  };
  const onContinue = () => {
    if (googleConfigured) return void go(() => signInWithGoogle({ turnstileToken: verification.getToken }));
    setDevOpen(true);
  };

  return (
    <div className="page">
      <nav className="d"><span className="brand">DEEP THIRD</span></nav>
      <div className="p center" style={{ paddingTop: "clamp(120px, 28vh, 260px)", maxWidth: 640 }}>
        {full ? <>
          <BetaFull />
          <p className="tiny mt24">Already submitted? Sign in to view your submission and review your reel.</p>
        </> : <>
          <div className="cap ink m-only">Deep Third</div>
          <h1 className="mt24">Every ball,<br className="m-only" /> from one camera.</h1>
          <p className="sub mt16">Upload a match. Analyze your performance, share highlights and study your rivals.</p>
        </>}
        <div className="mt48">
          <button className="btn dark wide" onClick={onContinue} disabled={busy || !verification.ready} data-testid="continue-google">{full ? "Sign in to your submission" : "Continue with Google"}</button>
        </div>
        {verification.element}
        <p className="tiny mt16" data-testid="signin-legal">No password. By continuing you agree to the <DocLink kind="beta_terms">Terms of use</DocLink>. Read our <DocLink kind="privacy">Privacy policy</DocLink>.</p>
        {!googleConfigured && (
          <p className="tiny mt24">Google sign-in placeholder: no client id is configured{dev.enabled ? ", so this signs in through the local dev backend." : "."}</p>
        )}
        {error ? <p className="err mt10" role="alert" data-testid="signin-error">{error}</p> : null}
        {devOpen && dev.enabled && (
          <form className="mt24 stack" style={{ gap: 10, maxWidth: 360, margin: "24px auto 0", textAlign: "left" }} onSubmit={(e) => { e.preventDefault(); void go(() => signInDev(email, plan)); }} data-testid="dev-login">
            <label className="tiny">Email (dev)<input className="field mt6" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
            <label className="tiny">Plan (dev)
              <select className="field mt6" value={plan} onChange={(e) => setPlan(e.target.value as "free" | "basic" | "pro")}>
                <option value="free">Free</option><option value="basic">Basic</option><option value="pro">Pro</option>
              </select>
            </label>
            <button className="btn full" type="submit" disabled={busy}>Sign in (dev)</button>
          </form>
        )}
      </div>
    </div>
  );
}
